import { useTranslations } from 'next-intl'

// Retreives the type of the translation key from the useTranslations hook

export type ITranslationKey =
    ReturnType<typeof useTranslations<never>> extends (
        key: infer K,
        // biome-ignore lint/suspicious/noExplicitAny: biome migration
        values?: any,
        // biome-ignore lint/suspicious/noExplicitAny: biome migration
        formats?: any
        // biome-ignore lint/suspicious/noExplicitAny: biome migration
    ) => any
        ? K
        : never

type ITElements = Record<
    'span' | 'p' | 'div' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'li',
    boolean
>

export type ITProps<T extends keyof ITElements> = {
    t: ITranslationKey | [ITranslationKey, Record<string, string | number>]
    className?: string
    style?: React.CSSProperties
    render?: (translation: string) => React.JSX.Element
} & {
    // With this type, the engineer can simply slap on the HTML tag they want (e.g. <T span t="some.translation" /> or <T p t="some.translation" />)
    [K in T]?: true
}

export const T = <T extends keyof ITElements>(props: ITProps<T>): React.JSX.Element | null => {
    const t = useTranslations()

    const tKey = Array.isArray(props.t) ? props.t[0] : props.t
    const tParams = Array.isArray(props.t) ? props.t[1] : undefined

    const translation = t(tKey, tParams)

    // biome-ignore lint/suspicious/noConsole: missing translation warning
    if (!translation) console.warn(`Translation for key "${props.t}" not found`)

    if (!translation) return null

    const defaultProps = {
        className: props.className,
        style: props.style,
    }

    if (props.render) return props.render(translation)

    switch (true) {
        case 'p' in props:
            return <p {...defaultProps}>{translation}</p>

        case 'div' in props:
            return <div {...defaultProps}>{translation}</div>

        case 'h1' in props:
            return <h1 {...defaultProps}>{translation}</h1>

        case 'h2' in props:
            return <h2 {...defaultProps}>{translation}</h2>

        case 'h3' in props:
            return <h3 {...defaultProps}>{translation}</h3>

        case 'h4' in props:
            return <h4 {...defaultProps}>{translation}</h4>

        case 'h5' in props:
            return <h5 {...defaultProps}>{translation}</h5>

        case 'h6' in props:
            return <h6 {...defaultProps}>{translation}</h6>

        case 'li' in props:
            return <li {...defaultProps}>{translation}</li>
        default:
            return <span {...defaultProps}>{translation}</span>
    }
}
